import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { onboardingService, OnbForm } from '../services/onboardingService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/CustomersList.css';
import '../styles/Pages.css';
import '../styles/InsightsPage.css';
import '../styles/OnboardingForms.css';

const IconBtn: React.FC<{ title: string; danger?: boolean; onClick?: () => void; children: React.ReactNode }> = ({ title, danger, onClick, children }) => (
  <button type="button" className={`onbf-action ${danger ? 'onbf-action-danger' : ''}`} title={title} onClick={onClick}>
    {children}
  </button>
);

const OnboardingForms: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const [search, setSearch] = useState('');
  const [forms, setForms] = useState<OnbForm[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<OnbForm | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!currentOrg?.id) return;
    onboardingService.listForms()
      .then(setForms)
      .catch(err => console.error('Failed to load forms:', err));
  }, [currentOrg?.id]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => forms.filter(f => !q || f.name.toLowerCase().includes(q)), [forms, q]);

  const duplicate = async (f: OnbForm) => {
    try {
      // The list omits `fields` for speed — fetch the full form before copying.
      const full = await onboardingService.getForm(f.id);
      const copy = await onboardingService.createForm({ name: `${f.name} (copy)`, headline: full?.headline ?? f.headline, fields: full?.fields ?? [] });
      setForms(prev => [copy, ...prev]);
    } catch (err: any) {
      console.error('Failed to duplicate form:', err);
      alert(`Failed to duplicate: ${err?.message ?? err}`);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setConfirmDelete(null);
    setForms(prev => prev.filter(f => f.id !== id));
    try { await onboardingService.deleteForm(id); }
    catch (err) { console.error('Failed to delete form:', err); }
  };

  const createForm = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const form = await onboardingService.createForm({ name, headline: 'Enter a headline', fields: [] });
      setShowCreate(false);
      setNewName('');
      navigate(`/onboarding/forms/${form.id}/edit`);
    } catch (err: any) {
      console.error('Failed to create form:', err);
      alert(`Failed to create form: ${err?.message ?? err}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div className="customers-list-container">
        <div className="customers-header-row">
          <h1 className="customers-title">Forms</h1>
          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input type="text" placeholder="Search forms..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
            </div>
            <button className="customers-add-btn btn-primary-cta" title="Create a new form" onClick={() => { setNewName(''); setShowCreate(true); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Create Form
            </button>
          </div>
        </div>

        <div className="onbf-wrap">
          <div className="onbf-row onbf-head">
            <span className="onbf-col-name">Name</span>
            <span className="onbf-col-actions">Actions</span>
          </div>

          {filtered.map(f => (
            <div key={f.id} className="onbf-row">
              <span className="onbf-col-name onbf-name">{f.name}</span>
              <span className="onbf-col-actions onbf-actions">
                <IconBtn title="Edit" onClick={() => navigate(`/onboarding/forms/${f.id}/edit`)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                </IconBtn>
                <IconBtn title="View" onClick={() => navigate(`/onboarding/forms/${f.id}/edit?preview=1`)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>
                </IconBtn>
                <IconBtn title="Duplicate" onClick={() => duplicate(f)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                </IconBtn>
                <IconBtn title="Delete" danger onClick={() => setConfirmDelete(f)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                </IconBtn>
              </span>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="onbf-empty">No forms yet. Click “Add Form” to create one.</div>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="onbf-confirm-backdrop" onClick={() => setShowCreate(false)}>
          <div className="onbf-create-card" onClick={e => e.stopPropagation()}>
            <div className="onbf-create-head">
              <h3>Create Form</h3>
              <button className="onbf-create-close" onClick={() => setShowCreate(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="onbf-create-body">
              <label className="onbf-create-label">Form name</label>
              <input
                className="onbf-create-input"
                autoFocus
                placeholder="e.g. Client Intake Form"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createForm(); if (e.key === 'Escape') setShowCreate(false); }}
              />
            </div>
            <div className="onbf-create-foot">
              <button className="onbf-confirm-cancel" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="onbf-create-save" disabled={!newName.trim() || creating} onClick={createForm}>{creating ? 'Creating…' : 'Create Form'}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="onbf-confirm-backdrop" onClick={() => setConfirmDelete(null)}>
          <div className="onbf-confirm-card" onClick={e => e.stopPropagation()}>
            <div className="onbf-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            </div>
            <h3 className="onbf-confirm-title">Delete this form?</h3>
            <p className="onbf-confirm-text"><strong>{confirmDelete.name}</strong> will be permanently removed. This action can’t be undone.</p>
            <div className="onbf-confirm-actions">
              <button className="onbf-confirm-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="onbf-confirm-delete" onClick={doDelete}>Delete form</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default OnboardingForms;
